import React, { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Gift, TrendingUp, TrendingDown, Filter } from "lucide-react";

const ACTIVITY = [
  { date: "24 Apr 2024 · 10:42 AM", ref: "#DY245676", type: "Earned for order", note: "Pharmacy & Wellness", points: 150, status: "Credited" },
  { date: "20 Apr 2024 · 04:15 PM", ref: "#DY245120", type: "Earned for order", note: "Health Supplements", points: 120, status: "Credited" },
  { date: "12 Apr 2024 · 11:07 AM", ref: "#DY243981", type: "Redeemed on coupon", note: "SAVE200", points: -200, status: "Redeemed" },
  { date: "10 Apr 2024 · 08:30 AM", ref: "#DY243070", type: "Earned for order", note: "Personal Care", points: 90, status: "Credited" },
  { date: "04 Apr 2024 · 02:11 PM", ref: "Referral Bonus", type: "Referral bonus", note: "Friend's first order", points: 250, status: "Credited" },
  { date: "10 Feb 2024 · 10:00 AM", ref: "#DY241008", type: "Earned for order", note: "Healthcare Devices", points: 100, status: "Credited" },
  { date: "05 Feb 2024 · 02:00 PM", ref: "Points Expiry", type: "Points expired", note: "Expired points", points: -50, status: "Expired" },
];

const MONTHLY = [
  { m: "Nov '23", earned: 240, redeemed: 100, expired: 0 },
  { m: "Dec '23", earned: 380, redeemed: 260, expired: 0 },
  { m: "Jan '24", earned: 460, redeemed: 220, expired: 30 },
  { m: "Feb '24", earned: 720, redeemed: 400, expired: 20 },
  { m: "Mar '24", earned: 620, redeemed: 340, expired: 0 },
  { m: "Apr '24", earned: 830, redeemed: 500, expired: 50 },
];

const TABS = ["All Activity", "Earned", "Redeemed", "Expired"];

export default function RewardHistory() {
  const [tab, setTab] = useState("All Activity");

  const rows = useMemo(() => {
    if (tab === "Earned") return ACTIVITY.filter((r) => r.status === "Credited");
    if (tab === "Redeemed") return ACTIVITY.filter((r) => r.status === "Redeemed");
    if (tab === "Expired") return ACTIVITY.filter((r) => r.status === "Expired");
    return ACTIVITY;
  }, [tab]);

  const totals = ACTIVITY.reduce(
    (acc, r) => {
      if (r.status === "Credited") acc.earned += r.points;
      if (r.status === "Redeemed") acc.redeemed += Math.abs(r.points);
      if (r.status === "Expired") acc.expired += Math.abs(r.points);
      return acc;
    },
    { earned: 0, redeemed: 0, expired: 0 }
  );
  const netChange = totals.earned - totals.redeemed - totals.expired;

  return (
    <div data-testid="account-reward-history" className="space-y-6">
      <header>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
          Reward History
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Track your points earnings, redemptions and activity
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-2xl bg-gradient-to-br from-[#4B2AAD] to-[#6B4CD9] text-white p-5">
          <p className="text-xs font-semibold uppercase tracking-wider opacity-90">
            Available Points
          </p>
          <div className="mt-1 flex items-center gap-2">
            <Gift className="h-6 w-6" />
            <p className="font-display text-3xl font-extrabold">2,450</p>
          </div>
          <p className="text-xs opacity-90 mt-1">₹245.00 value</p>
          <p className="text-[11px] opacity-80 mt-2">1 Point = ₹0.10</p>
        </div>
        <StatCard
          icon={TrendingUp}
          label="Points Earned"
          value={`+ ${totals.earned.toLocaleString()}`}
          note="This Year"
          tone="green"
        />
        <StatCard
          icon={TrendingDown}
          label="Points Redeemed"
          value={`- ${totals.redeemed.toLocaleString()}`}
          note="This Year"
          tone="red"
        />
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden">
        <div className="flex items-center justify-between flex-wrap gap-3 px-4 pt-4">
          <div className="flex gap-1 bg-[#F5F3FB] rounded-xl p-1">
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                data-testid={`rh-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  tab === t
                    ? "bg-white text-[#4B2AAD] shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>Apr 01, 2024 – Apr 30, 2024</span>
            <button className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 hover:border-[#4B2AAD]">
              <Filter className="h-3 w-3" /> Filters
            </button>
          </div>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F3FB] text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Date</th>
                <th className="text-left px-4 py-2 font-semibold">Order / Reference</th>
                <th className="text-left px-4 py-2 font-semibold">Activity</th>
                <th className="text-right px-4 py-2 font-semibold">Points</th>
                <th className="text-right px-4 py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-gray-100 text-gray-700">
                  <td className="px-4 py-3 text-xs whitespace-nowrap">{r.date}</td>
                  <td className="px-4 py-3 text-xs font-semibold text-[#4B2AAD]">{r.ref}</td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-gray-900">{r.type}</p>
                    <p className="text-xs text-gray-500">{r.note}</p>
                  </td>
                  <td
                    className={`px-4 py-3 text-right font-bold ${
                      r.points >= 0 ? "text-emerald-600" : "text-red-500"
                    }`}
                  >
                    {r.points >= 0 ? `+${r.points}` : r.points}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${
                        r.status === "Credited"
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                          : r.status === "Redeemed"
                            ? "bg-[#F3EFFC] text-[#4B2AAD] border border-[#4B2AAD]/20"
                            : "bg-red-50 text-red-500 border border-red-100"
                      }`}
                    >
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-display text-base font-bold text-gray-900">
            Monthly Activity Overview
          </h3>
          <div className="flex items-center gap-4 text-xs">
            <LegendDot color="#4B2AAD" label={`Earned ${totals.earned.toLocaleString()}`} />
            <LegendDot color="#F43F5E" label={`Redeemed ${totals.redeemed.toLocaleString()}`} />
            <LegendDot color="#FFB020" label={`Expired ${totals.expired.toLocaleString()}`} />
            <span className={`font-semibold ${netChange >= 0 ? "text-emerald-600" : "text-red-500"}`}>
              Net Change {netChange >= 0 ? "+" : ""}
              {netChange.toLocaleString()}
            </span>
          </div>
        </div>
        <div className="mt-4 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={MONTHLY}>
              <XAxis dataKey="m" tick={{ fontSize: 12, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#6b7280" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "#F3EFFC" }} />
              <Legend wrapperStyle={{ display: "none" }} />
              <Bar dataKey="earned" fill="#4B2AAD" radius={[6, 6, 0, 0]} />
              <Bar dataKey="redeemed" fill="#F43F5E" radius={[6, 6, 0, 0]} />
              <Bar dataKey="expired" fill="#FFB020" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, note, tone }) {
  const toneCls =
    tone === "green"
      ? "text-emerald-600"
      : tone === "red"
        ? "text-red-500"
        : "text-gray-900";
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#F3EFFC] flex items-center justify-center">
          <Icon className="h-5 w-5 text-[#4B2AAD]" />
        </div>
        <div>
          <p className="text-xs text-gray-500">{label}</p>
          <p className={`font-display text-2xl font-extrabold ${toneCls}`}>{value}</p>
          <p className="text-[11px] text-gray-500">{note}</p>
        </div>
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1 text-gray-600">
      <span className="h-2.5 w-2.5 rounded-full inline-block" style={{ background: color }} />
      {label}
    </span>
  );
}
